"use client"

import React, { useContext, useEffect, useState, useMemo, useCallback } from "react"
import { useParams, Link } from "react-router-dom"
import axios from "axios"
import { CircuitoContext } from "../../context/CircuitoContext"
import { DomainContext } from "./LayoutCircuito"
import { FaHome, FaClock, FaWhatsapp, FaFacebook, FaCopy, FaCheck, FaChevronRight } from "react-icons/fa"
import { FaXTwitter } from "react-icons/fa6"
import "../../styles/circuitoHomeV2.css"

const API = "https://api.ticketdovaqueiro.com"

function formatDate(dateString) {
  if (!dateString) return ""
  return new Date(dateString).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "short", year: "numeric",
  }).toUpperCase().replace(/\./g, "")
}

function timeAgo(dateString) {
  if (!dateString) return "Agora"
  const diff = Date.now() - new Date(dateString).getTime()
  const min = Math.floor(diff / 60000)
  const h   = Math.floor(min / 60)
  const d   = Math.floor(h / 24)
  if (d >= 30) return `${Math.floor(d / 30)} mês(es) atrás`
  if (d >= 1)  return `${d} dia(s) atrás`
  if (h >= 1)  return `${h} hora(s) atrás`
  if (min >= 1) return `${min} min atrás`
  return "Agora"
}

const CAT_LABELS = {
  destaque: "Destaque", vaquejadas: "Vaquejadas",
  campeonato: "Campeonatos", ranking: "Ranking",
  avisos: "Avisos", geral: "Geral",
}

export default function CircuitoNoticiaDetalhe() {
  const { noticiaId }           = useParams()
  const { circuito }            = useContext(CircuitoContext)
  const { basePath, slugAtivo } = useContext(DomainContext)

  const [noticia, setNoticia]   = useState(null)
  const [recentes, setRecentes] = useState([])
  const [loading, setLoading]   = useState(true)
  const [erro, setErro]         = useState("")
  const [copied, setCopied]     = useState(false)

  useEffect(() => {
    const carregar = async () => {
      try {
        setLoading(true); setErro("")
        const resCircuito = await axios.get(`${API}/api/circuitos/slug/${slugAtivo}`)
        const circuitoId  = resCircuito.data?.circuito?._id
        if (!circuitoId) throw new Error("Circuito não encontrado")

        const [resNoticia, resLista] = await Promise.all([
          axios.get(`${API}/api/circuitos/${circuitoId}/noticias/${noticiaId}`),
          axios.get(`${API}/api/circuitos/${circuitoId}/noticias`),
        ])
        setNoticia(resNoticia.data)
        setRecentes((resLista.data || []).filter(n => n._id !== noticiaId).slice(0, 6))
      } catch (e) {
        console.error(e); setErro("Não foi possível carregar a notícia.")
      } finally {
        setLoading(false)
      }
    }
    carregar()
  }, [slugAtivo, noticiaId])

  const imgUrl = useMemo(() => {
    if (!noticia?.imagem) return null
    return `${API}/uploads/${noticia.imagem}`
  }, [noticia?.imagem])

  const pageUrl    = typeof window !== "undefined" ? window.location.href : ""
  const titleEnc   = encodeURIComponent(noticia?.titulo || "")
  const pageUrlEnc = encodeURIComponent(pageUrl)

  const copyLink = useCallback(() => {
    navigator.clipboard?.writeText(pageUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    })
  }, [pageUrl])

  if (loading) return (
    <div className="ch-page">
      <div className="ch-section">
        <div className="ch-container"><div className="ch-loading">Carregando notícia...</div></div>
      </div>
    </div>
  )

  if (erro) return (
    <div className="ch-page">
      <div className="ch-section">
        <div className="ch-container"><div className="ch-empty">{erro}</div></div>
      </div>
    </div>
  )

  return (
    <div className="ch-page">

      {/* ── Cabeçalho da notícia (full-width) ── */}
      <div className="ch-article-header-section">
        <div className="ch-container">
          <nav className="ch-breadcrumb">
            <FaHome size={12} />
            <Link to={`${basePath}/`}>Início</Link>
            <span>/</span>
            <Link to={`${basePath}/noticias`}>Notícias</Link>
            <span>/</span>
            <span>Detalhe</span>
          </nav>

          <h1 className="ch-article-title-big">{noticia?.titulo}</h1>

          <div className="ch-article-meta-row">
            <span className="ch-date-badge">
              <FaClock size={10} />
              {formatDate(noticia?.createdAt)}
            </span>
            {noticia?.categoria && noticia.categoria !== "geral" && (
              <span className="ch-cat-badge">
                {CAT_LABELS[noticia.categoria] || noticia.categoria}
              </span>
            )}
            {circuito?.nome && (
              <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem" }}>
                Por {circuito.nome}
              </span>
            )}
          </div>

          {noticia?.subtitulo && (
            <p className="ch-article-lead-text">{noticia.subtitulo}</p>
          )}
        </div>
      </div>

      {/* ── Imagem hero (full-width) ── */}
      {imgUrl && (
        <div className="ch-article-hero">
          <img src={imgUrl} alt={noticia?.titulo} className="ch-article-hero__img" />
        </div>
      )}

      {/* ── 3 colunas: share | conteúdo | sidebar ── */}
      <div className="ch-container">
        <div className="ch-article-layout">

          {/* Coluna de compartilhamento */}
          <aside className="ch-article-share">
            <span className="ch-share-label">Compartilhar</span>
            <div className="ch-share-divider" />
            <a
              href={`https://wa.me/?text=${titleEnc}%20${pageUrlEnc}`}
              target="_blank" rel="noopener noreferrer"
              className="ch-share-btn ch-share-btn--wa"
              title="Compartilhar no WhatsApp"
            >
              <FaWhatsapp />
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${pageUrlEnc}`}
              target="_blank" rel="noopener noreferrer"
              className="ch-share-btn ch-share-btn--fb"
              title="Compartilhar no Facebook"
            >
              <FaFacebook />
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=${titleEnc}&url=${pageUrlEnc}`}
              target="_blank" rel="noopener noreferrer"
              className="ch-share-btn ch-share-btn--tw"
              title="Compartilhar no X"
            >
              <FaXTwitter />
            </a>
            <div className="ch-share-divider" />
            <button
              onClick={copyLink}
              className={`ch-share-btn ch-share-btn--copy ${copied ? "copied" : ""}`}
              title="Copiar link"
            >
              {copied ? <FaCheck size={14} /> : <FaCopy size={14} />}
            </button>
          </aside>

          {/* Conteúdo do artigo */}
          <main className="ch-article-main">
            <div
              className="ch-article-body"
              dangerouslySetInnerHTML={{ __html: noticia?.conteudoHtml || "<p>Sem conteúdo.</p>" }}
            />

            {/* Outras notícias (abaixo do artigo) */}
            {recentes.length > 0 && (
              <div className="ch-related-section">
                <div className="ch-related-section__header">
                  <div className="ch-related-section__bar" />
                  <h3 className="ch-related-section__title">Outras Notícias</h3>
                </div>
                <div className="ch-news-cards-grid">
                  {recentes.slice(0, 3).map(n => (
                    <Link key={n._id} to={`${basePath}/noticias/${n._id}`} className="ch-news-card-v2">
                      <div className="ch-news-card-v2__img-wrap">
                        <img
                          src={n.imagem ? `${API}/uploads/${n.imagem}` : "https://via.placeholder.com/600x360?text=Notícia"}
                          alt={n.titulo}
                          className="ch-news-card-v2__img"
                        />
                      </div>
                      <div className="ch-news-card-v2__body">
                        <p className="ch-news-card-v2__title">{n.titulo}</p>
                        {n.subtitulo && <p className="ch-news-card-v2__subtitle">{n.subtitulo}</p>}
                        <div className="ch-news-card-v2__footer">
                          <span className="ch-news-card-v2__date">{formatDate(n.createdAt)}</span>
                          <FaChevronRight size={10} style={{ color: "rgba(255,255,255,0.3)" }} />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </main>

          {/* Sidebar direita */}
          <aside className="ch-article-sidebar">
            {recentes.length > 0 && (
              <div className="ch-widget">
                <div className="ch-widget__header">
                  <div className="ch-widget__accent" />
                  <h4 className="ch-widget__title">Recentes</h4>
                </div>
                <div className="ch-widget__list">
                  {recentes.map(n => (
                    <Link key={n._id} to={`${basePath}/noticias/${n._id}`} className="ch-recent-item">
                      <div className="ch-recent-item__thumb">
                        <img
                          src={n.imagem ? `${API}/uploads/${n.imagem}` : "https://via.placeholder.com/144x108?text=+"}
                          alt={n.titulo}
                        />
                      </div>
                      <div className="ch-recent-item__content">
                        <p className="ch-recent-item__title">{n.titulo}</p>
                        <span className="ch-recent-item__date">{formatDate(n.createdAt)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>

        </div>
      </div>
    </div>
  )
}